import type { Source } from "./api.js";
import { LIMITS } from "./draft.js";

/** What the owner can filter the document list by. */
export type DocFilter = "all" | "ready" | "working" | "attention" | "unsupported";

export const PAGE_SIZE = 25;

const group = (s: Source): Exclude<DocFilter, "all"> => (s.status === "indexed" ? "ready" : s.status === "pending" || s.status === "indexing" ? "working" : s.status === "error" ? "attention" : "unsupported");

export interface DocCounts {
  total: number;
  ready: number;
  working: number;
  attention: number;
  unsupported: number;
}

export function countDocs(docs: Source[]): DocCounts {
  const c: DocCounts = { total: docs.length, ready: 0, working: 0, attention: 0, unsupported: 0 };
  for (const d of docs) c[group(d)]++;
  return c;
}

const plain = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Name search (case and accents ignored) and status group. Order is kept. */
export function filterDocs(docs: Source[], q: string, filter: DocFilter): Source[] {
  const needle = plain(q.trim());
  return docs.filter((d) => (filter === "all" || group(d) === filter) && (needle === "" || plain(d.name).includes(needle)));
}

export function pageCount(total: number, size = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

/** Page numbers start at 1; a number out of range is moved into range. */
export function pageOf<T>(items: T[], page: number, size = PAGE_SIZE): { items: T[]; page: number; pages: number; from: number; to: number } {
  const pages = pageCount(items.length, size);
  const p = Math.min(Math.max(1, page), pages);
  const start = (p - 1) * size;
  const slice = items.slice(start, start + size);
  return { items: slice, page: p, pages, from: items.length === 0 ? 0 : start + 1, to: start + slice.length };
}

/**
 * Runs `work` on every item with at most `limit` running at once (uploads and deletes of hundreds of documents must not open
 * hundreds of requests together). `shouldStop` is checked before each item starts; `onProgress` gets the number finished.
 * One item failing does not stop the others; failures are returned.
 */
export async function runPool<T>(items: T[], limit: number, work: (item: T) => Promise<void>, opts: { shouldStop?: () => boolean; onProgress?: (done: number) => void } = {}): Promise<{ done: number; failed: { item: T; error: unknown }[]; stopped: boolean }> {
  let next = 0;
  let done = 0;
  let stopped = false;
  const failed: { item: T; error: unknown }[] = [];
  const lane = async () => {
    while (next < items.length) {
      if (opts.shouldStop?.()) {
        stopped = true;
        return;
      }
      const item = items[next++] as T;
      try {
        await work(item);
      } catch (error) {
        failed.push({ item, error });
      }
      done++;
      opts.onProgress?.(done);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, lane));
  return { done, failed, stopped };
}

// ---- files ---------------------------------------------------------------------------------------------------------------
export const TEXT_EXT = ["txt", "md", "markdown", "csv", "tsv", "html", "htm"];
/** Sent as bytes. The server explains the ones it cannot read (old .doc/.xls, slides, images when no reader is set up). */
export const BINARY_EXT = ["pdf", "docx", "xlsx", "doc", "xls", "ppt", "pptx", "jpg", "jpeg", "png", "webp"];
export const MAX_FILE_MB = LIMITS.fileMaxMB;
export const extOf = (n: string) => n.split(".").pop()?.toLowerCase() ?? "";

export type FileVerdict = "text" | "binary" | "too_big" | "unsupported_type";
export function classifyFile(file: { name: string; size: number }): FileVerdict {
  if (file.size > MAX_FILE_MB * 1024 * 1024) return "too_big";
  const e = extOf(file.name);
  return TEXT_EXT.includes(e) ? "text" : BINARY_EXT.includes(e) ? "binary" : "unsupported_type";
}
